import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useMeetingActionsQuery, useUpdateMeetingActionStatusMutation } from "../api/queries";
import type { MeetingActionListItem } from "@/modules/management/meeting-minutes/domain/types";
import type { MeetingActionsSearchParams } from "../ui/MeetingActionsFilter";
import { ActionDetailModal } from "../ui/ActionDetailModal";
import { MeetingActionsFilter } from "../ui/MeetingActionsFilter";
import { MeetingActionsTable } from "../ui/MeetingActionsTable";
import { Button, confirm, toast } from "@devhop/ui";
import { HelpCircle } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import {
  MeetingTour,
  meetingActionsTourSteps,
  useMeetingTour,
} from "../tour";

export function MeetingActionsPage() {
  const nav = useNavigate({ from: "/app/management/meetings/actions" });
  const search = useSearch({
    from: "/app/management/meetings/actions",
  }) as MeetingActionsSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const sortBy = search.sort?.[0]?.field ?? "dueDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "asc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      dueDateFrom: search.dueDateFrom,
      dueDateTo: search.dueDateTo,
      status: search.status,
      assigneeId: search.assigneeId,
      meetingId: search.meetingId,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.dueDateFrom,
      search.dueDateTo,
      search.status,
      search.assigneeId,
      search.meetingId,
    ],
  );

  const listQuery = useMeetingActionsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const canUpdate = usePermissions().has(Permissions.mgmt_meeting.update);
  const tour = useMeetingTour("actions");
  const updateStatusMutation = useUpdateMeetingActionStatusMutation();
  const [selectedAction, setSelectedAction] =
    useState<MeetingActionListItem | null>(null);

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        dueDateFrom: search.dueDateFrom,
        dueDateTo: search.dueDateTo,
        status: search.status,
        assigneeId: search.assigneeId,
        meetingId: search.meetingId,
      },
    });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          dueDateFrom: search.dueDateFrom,
          dueDateTo: search.dueDateTo,
          status: search.status,
          assigneeId: search.assigneeId,
          meetingId: search.meetingId,
        },
      });
    },
    [
      nav,
      search.limit,
      search.dueDateFrom,
      search.dueDateTo,
      search.status,
      search.assigneeId,
      search.meetingId,
    ],
  );

  const handleViewMeeting = (row: MeetingActionListItem) => {
    nav({ to: "/app/management/meetings/$id", params: { id: row.meetingId } });
  };

  const handleMarkDone = useCallback(
    async (row: MeetingActionListItem) => {
      if (row.status === "done") return;
      const ok = await confirm({
        title: "ຢືນຢັນການອັບເດດສະຖານະ",
        description:
          "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການອັບເດດສະຖານະປະເດັນນີ້ເປັນ ສຳເລັດ?",
        actionText: "ຕົກລົງ",
        cancelText: "ຍົກເລີກ",
      });
      if (!ok) return;
      updateStatusMutation.mutate(
        { meetingId: row.meetingId, actionId: row.id, status: "done" },
        {
          onSuccess: () => toast.success("ອັບເດດສະຖານະເປັນສຳເລັດແລ້ວ"),
          onError: () => toast.error("ບໍ່ສາມາດອັບເດດໄດ້"),
        },
      );
    },
    [updateStatusMutation],
  );

  return (
    <>
      <Header />
      <Main>
        <MeetingTour
          steps={meetingActionsTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center gap-2"
          data-tourid="meeting-actions-header"
        >
          <h1 className="font-semibold text-2xl">ຕິດຕາມປະເດັນທີ່ລົມກັນ</h1>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 rounded-full"
            onClick={tour.startTour}
            title="ເລີ່ມທົດສອບການນຳທາງ"
            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
          >
            <HelpCircle className="size-5" />
          </Button>
        </div>
        <p className="text-muted-foreground mb-6 text-sm">
          ລາຍການປະເດັນຈາກບັນທຶກການປະຊຸມ ວັນສົ່ງ ສະຖານະ ຜູ້ຮັບຜິດຊອບ
        </p>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="meeting-actions-filter"
        >
          <MeetingActionsFilter
            searchParams={{ ...search, limit: search.limit ?? 20 }}
            fromRoute="/app/management/meetings/actions"
          />
          <div data-tourid="meeting-actions-table">
            <MeetingActionsTable
              data={data}
            isLoading={listQuery.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onViewMeeting={handleViewMeeting}
            onViewDetail={(row) => setSelectedAction(row)}
            onMarkDone={canUpdate ? handleMarkDone : undefined}
            />
          </div>
        </div>

        <ActionDetailModal
          open={selectedAction !== null}
          onOpenChange={(open) => !open && setSelectedAction(null)}
          action={selectedAction}
          onMarkDone={canUpdate ? handleMarkDone : undefined}
        />
      </Main>
    </>
  );
}
